// submitForm dispatcher + trial / Stripe / Authorize.net payment paths
// and the Authorize.net Salesforce insertion chains.
//
// Extracted verbatim from DealerForm.vue; merged back via `mixins`.
import { postMembership, patchMembership } from "@/services/api/membership";
import { postAccount, patchAccount } from "@/services/api/account";
import {
  postContact,
  patchContact,
  postContactRole,
} from "@/services/api/contact";
import { postBoat, patchBoat } from "@/services/api/boat";
import {
  postOpportunity,
  postOpportunityLineItem,
} from "@/services/api/opportunity";
import { postPayment } from "@/services/api/payment";
import { postTrialMember } from "@/services/api/trial";
import {
  postAuthorizenetTransaction,
  postAuthorizenetArb,
  patchAuthorizenetArb,
  deleteAuthorizenetArb,
} from "@/services/api/authorizenet";
import { postEmail } from "@/services/api/email";
import { postItfmaLogging } from "@/services/api/logging";
import { cancelArbs } from "@/services/api/utility";
import {
  STRIPE_PROCESSOR_CONNECTION_SFID,
  RECORDTYPE_PERSON_CONTACT,
} from "@/config/salesforceIds";
import { MEMBERSHIP_DISPLAY_ONLY_FIELDS } from "../constants";

export default {
  methods: {
    async submitForm() {
      await this.ensureUser();

      this.trackUxcamSubmit("started", {
        arb_enroll_only: this.isArbEnrollOnly === true,
      });

      if (this.isArbEnrollOnly) {
        return this.enrollExistingMemberInArb();
      }

      console.log("this.funds_collected_locally", this.funds_collected_locally);

      // Calculate Cart Price at start so that we always send the right price to the invoice.
      this.price_total = this.calculateCartPrice();
      this.$v.$touch();

      //Calculate Cart Price again to ensure accuracy
      this.price_total = this.calculateCartPrice();

      if (this.$v.$invalid) {
        this.trackUxcamSubmit("blocked", { reason: "validation" });
        this.$bvToast.toast(
          "Something isn't right with the form. Please validate each field.",
          {
            variant: "warning",
            title: "Validation error.",
            autoHideDelay: 5000,
          }
        );
        return;
      }

      // turn above statement into json to send to the server
      const formData = JSON.stringify(this.$data);

      //send the formData to api_url/v1/itfma_logging/:username
      postItfmaLogging(this.getName, formData).then((response) => {
        console.log(response);
      });

      this.disableSubmit = true;
      this.assignAOR();
      this.cc_declined = false;

      // activation_codes is always a populated object (fields default to null),
      // so the old `!activation_codes || .length === 0` check was dead code and
      // never diverted $0 submissions to the trial path. Use the real "no code
      // applied" signal: the activation code's name is null/empty.
      const hasActivationCode = !!this.activation_codes?.name;
      if (
        !hasActivationCode &&
        (this.price_total == 0 || this.CardSelection.includes("Trial")) &&
        this.promotion_code?.toUpperCase() !== "FSI"
      ) {
        return this.submitAsTrialMember();
      }

      //else it's a full member
      if (
        this.shipping_same_as_billing == "true" ||
        this.shipping_same_as_billing == true
      ) {
        this.account.shippingstreet = this.account.billingstreet;
        this.account.shippingstate = this.account.billingstate;
        this.account.shippingcity = this.account.billingcity;
        this.account.shippingpostalcode = this.account.billingpostalcode;
        this.account.shippingcountry = this.account.billingcountry;
      }

      const opp_guid = this.pendingOppGuid || this.guid();
      this.pendingOppGuid = opp_guid;

      // Check if credit card payment is required
      if (this.CreditCardRequired) {
        return this.processStripePayment(opp_guid);
      }

      // Traditional Authorize.net flow
      this.processAuthorizenetPayment(opp_guid);
    },
    submitAsTrialMember() {
      // The body is the whole component state, so a loaded member's ids would
      // ride along and the server would update that member instead of inserting.
      const payload = { ...this.$data, isRenew: this.isRenew };
      if (!this.isRenew) {
        payload.contact_sfid = null;
        payload.boat_sfid = null;
        payload.membership_sfid = null;
        payload.account_sfid = null;
        payload.arbs_sfid = null;
        payload.routes = {
          contacts: null,
          boats: null,
          memberships: null,
          account: null,
          arbs: null,
        };
      }

      const formData = JSON.stringify(payload);

      let trialError = false;
      let trialInserted = false;

      postTrialMember(formData)
        .then((response) => {
          // check if status is 200
          if (response["status"] == 200) {
            if (
              response["data"]["error"] ===
              "Member already has an active trial"
            ) {
              trialError = true;
              this.trackUxcamSubmit("blocked", {
                path: "trial",
                reason: "active_trial_exists",
              });
              //display toast
              this.$bvToast.toast(
                "This member already has an active trial.",
                {
                  title: "Member already has an active trial",
                  variant: "danger",
                  autoHideDelay: 5000,
                }
              );
              this.disableSubmit = false;
              return;
            }
            trialInserted = true;
            this.trackUxcamSubmit("succeeded", { path: "trial" });
            this.$bvToast.toast(
              "The trial member was inserted successfully.",
              {
                title: `The trial member was inserted successfully.`,
                autoHideDelay: 5000,
                variant: "success",
              }
            );
            console.log(response);
          } //else if status is 400 and "error" in present in return dict
        })
        .then((response) => {
          if (trialError) {
            return;
          }
          //Send email stating that this was a success (or if it was a failure)
          console.log(response);

          let data = {};
          data["to_list"] = this.getEmailList;
          data["body"] =
            this.account.name +
            " has been successfully submitted into the database." +
            "\n\n" +
            this.getEmailBody;
          data["subject"] =
            "Lead successfully inserted from Membership App";

          console.log("attempting to send email2");
          console.log(data);

          return postEmail(data)
            .then((response) => {
              console.log(response);
            })
            .catch((error) => {
              console.error("Trial confirmation email failed:", error);
            });
        })
        .catch((error) => {
          console.error("Trial submission failed:", error);
          this.trackUxcamSubmit("failed", {
            path: "trial",
            message: error?.message,
          });
          this.$bvToast.toast(
            "The trial member could not be submitted. Please try again.",
            {
              title: "Trial submission failed",
              variant: "danger",
              autoHideDelay: 8000,
            }
          );
        })
        .finally(() => {
          // Reset on a successful insert even if the email failed. Failures keep
          // the state so the CSR can retry.
          if (trialInserted) {
            this.clearForm();
          }
          this.disableSubmit = false;
        });
    },
    processStripePayment(opp_guid) {
      // If we already have a confirmed payment, skip charging and go straight to SF insertion
      if (this.stripePaymentIntentId && this.transId) {
        console.log(
          "Payment already confirmed (idempotent retry), skipping charge. PaymentIntent:",
          this.stripePaymentIntentId
        );
        this.continueSubmissionAfterPayment(opp_guid);
        return;
      }

      // Process Stripe payment
      this.$refs.stripePaymentForm
        .processPayment()
        .then((result) => {
          console.log("Stripe payment result:", result);
          if (result.success) {
            // Store the payment intent ID as transaction ID
            this.transId = result.transId;
            this.stripePaymentIntentId = result.paymentIntentId;
            this.stripeCustomerId = result.customerId;
            this.stripePaymentMethodId = result.paymentMethodId;
            this.stripeCardBrand = result.cardBrand || "Unknown";
            this.stripeCardLast4 = result.cardLast4 || "";
            this.stripeExpMonth = result.expMonth;
            this.stripeExpYear = result.expYear;
            this.cc_declined = false;

            console.log("Stripe Customer ID:", this.stripeCustomerId);
            console.log(
              "Stripe Payment Method ID:",
              this.stripePaymentMethodId
            );

            // Clear Stripe session state after successful payment
            // Don't try to cancel - payment already succeeded
            if (this.$refs.stripePaymentForm) {
              this.$refs.stripePaymentForm.clientSecret = null;
              this.$refs.stripePaymentForm.setupIntentId = null;
            }

            this.$bvToast.toast(
              "Card successfully charged via Stripe. Proceeding with account creation.",
              {
                title: "Payment successful",
                variant: "success",
                autoHideDelay: 5000,
              }
            );

            // Continue with the rest of the submission logic
            this.continueSubmissionAfterPayment(opp_guid);
          } else {
            this.$bvToast.toast(`${result.error}`, {
              title: "There was an error processing payment.",
              variant: "danger",
              autoHideDelay: 5000,
            });

            this.cc_declined = true;
            this.trackUxcamSubmit("payment_declined", {
              processor: "stripe",
              message: result.error?.message,
            });
            this.disableSubmit = false;
            return;
          }
        })
        .catch((error) => {
          console.error("Stripe payment error:", error);
          this.$bvToast.toast(
            "There was an error processing your payment. Please try again.",
            {
              title: "Payment failed",
              autoHideDelay: 10000,
              variant: "danger",
            }
          );
          this.cc_declined = true;
          this.trackUxcamSubmit("payment_declined", {
            processor: "stripe",
            message: error?.message,
          });
          this.disableSubmit = false;
        });
    },
    processAuthorizenetPayment(opp_guid) {
      // Traditional Authorize.net flow
      let authorize_data = {};
      authorize_data["cc_number"] = this.memberships.card_number__c;
      authorize_data["exp_date"] = this.authnet_expiration;
      authorize_data["ccv"] = this.memberships.card_security_code__c;
      authorize_data["first_name"] = this.contacts.firstname;
      authorize_data["last_name"] = this.contacts.lastname;
      authorize_data["amount"] = (
        Math.round(this.price_total * 100) / 100
      ).toFixed(2);
      authorize_data["email"] = this.contacts.email;
      authorize_data["street_address"] = this.account.billingstreet;
      authorize_data["city"] = this.account.billingcity;
      authorize_data["state"] = this.account.billingstate;
      authorize_data["zip"] = this.account.billingpostalcode;
      authorize_data["country"] = this.account.billingcountry;
      authorize_data["company"] = "";
      authorize_data["uuid"] = opp_guid;
      authorize_data["order_desc"] = "Payment via Membership App";

      console.log(authorize_data);

      if (!this.CreditCardRequired) {
        //optional flag skips authorization of cc
        authorize_data["optional_flag"] = true;
      }

      postAuthorizenetTransaction(authorize_data)
        .then((response) => {
          console.log("CHARGE credit card response: ");
          console.log(response);
          if (response["data"]["status"] == "error") {
            console.log("transaction declined");

            this.$bvToast.toast(`${response["data"]["message"]}`, {
              title: "There was an error processing payment.",
              variant: "danger",
              autoHideDelay: 5000,
            });

            this.cc_declined = true;
            this.trackUxcamSubmit("payment_declined", {
              processor: "authorizenet",
            });
            return;
          } else if (response["data"]["status"] == "skipped") {
            this.$bvToast.toast(`Payment insertion skipped.`, {
              variant: "info",
              title:
                "Funds collected locally. Proceeding with account creation.",
              autoHideDelay: 5000,
            });

            this.cc_declined = false;
          } else {
            this.$bvToast.toast(
              "Card successfully charged. Proceeding with account creation.",
              {
                title: "Authorization status update",
                variant: "success",
                autoHideDelay: 5000,
              }
            );

            this.cc_declined = false;
          }

          // get transid and authid
          console.log(response);

          this.auth_code = response["data"]["auth_code"];
          this.transId = response["data"]["transId"];
        })
        .catch((error) => {
          console.log(error);
          this.$bvToast.toast(
            "There was an error processing your payment. Please try again.",
            {
              title: "The card was declined or is invalid",
              autoHideDelay: 10000,
              variant: "danger",
            }
          );

          this.cc_declined = true;
          this.trackUxcamSubmit("payment_declined", {
            processor: "authorizenet",
            message: error?.message,
          });
          this.disableSubmit = false;
          return;
        })
        .then(() => this.finishAuthorizenetSubmission(opp_guid));
    },
    async finishAuthorizenetSubmission(opp_guid) {
      //cancel existing ARB if they don't want it in SALESFORCE
      console.log(`this.arbs.sfid = ${this.arbs.sfid}`);
      if (!this.autorenew_status && this.arbs.sfid !== null) {
        data = {};
        data["sfid"] = this.arbs.sfid;
        // TODO: Add code ot cancel ARB?
        patchAuthorizenetArb(data).then((response) => {
          if (response["data"]["response"] != "success") {
            console.log(
              "something went wrong when trying to cancel the existing ARB in auth.net"
            );
          } else {
            console.log(`arb successfully cancelled`);
          }
        });
      }

      if (this.cc_declined == false) {
        console.log("cc not declined, starting ARB");
        if (this.autorenew_status == true) {
          console.log("arb selected..");

          //if auto-renew is checked, check to see if ARB is active, if it is cancel old ARB then create new ARB
          if (this.arbs.sfid !== undefined && this.arbs.sfid != null) {
            console.log("they already have an arb");
            //cancel ARB in salesforce
            if (this.arbs.pymt__subscription_status__c == "Active") {
              console.log("ACTIVE ARB DETECTED");
              var data = {};
              data["subscriptionId"] =
                this.arbs.pymt__authnet_subscription_id__c;
              // Delete ARB if it exists
              deleteAuthorizenetArb(arb_data).then((response) => {
                if (response["data"]["response"] != "success") {
                  console.log(
                    "something went wrong when trying to cancel the existing ARB in auth.net"
                  );
                } else {
                  console.log(`arb successfully cancelled`);
                }
              });
            }
          } else {
            console.log(
              "ARB Status is not active, so no need to cancel."
            );
          }

          console.log("CREATE NEW ARB");

          //then create new arb
          let arb_data = {};
          arb_data["sub_name"] =
            "ARB Subscription Profile - Membership App";
          arb_data["cc_number"] = this.memberships.card_number__c;
          arb_data["exp_date"] = this.authnet_expiration;
          arb_data["ccv"] = this.memberships.card_security_code__c;
          arb_data["first_name"] = this.contacts.firstname;
          arb_data["last_name"] = this.contacts.lastname;
          arb_data["amount"] = (
            Math.round(
              (this.price_total - this.donation_amount) * 100
            ) / 100
          ).toFixed(2);
          arb_data["trial_amount"] = "0";
          arb_data["invoice_guid"] = opp_guid;
          arb_data["email"] = this.contacts.email;

          //break expiration date into proper format

          var today = new Date();
          var dd = String(today.getDate()).padStart(2, "0");
          var mm = String(today.getMonth() + 1).padStart(2, "0"); //January is 0!
          var yyyy = today.getFullYear();

          today = mm + "/" + dd + "/" + yyyy;

          //convert existing exp date to proper format for API
          var correctForAPIDate = null;
          if (this.isRenew) {
            let exp_date = Date.parse(
              this.membership_expiration_date__c
            );
            var asDate = new Date(exp_date);

            var year = asDate.getFullYear();
            var month = String(asDate.getMonth() + 1).padStart(2, "0");
            var day = String(asDate.getDate()).padStart(2, "0");

            correctForAPIDate = month + "/" + day + "/" + year;
          }

          arb_data["member_exp_date"] = this.isRenew
            ? correctForAPIDate
            : today;

          console.log("MEMBER EXPIRATION DATE:");
          console.log(arb_data["member_exp_date"]);
          //pass existing expiration date
          //pass promotion number of days

          console.log("Axios post..");
          postAuthorizenetArb(arb_data);
        }

        console.log("Axios post end");

        var account_parsed_obj = JSON.parse(
          JSON.stringify(this.account)
        );
        var account_keynames = Object.keys(account_parsed_obj);

        var contact_parsed_obj = JSON.parse(
          JSON.stringify(this.contacts)
        );
        var contact_keynames = Object.keys(contact_parsed_obj);

        var boat_parsed_obj = JSON.parse(JSON.stringify(this.boats));
        var boat_keynames = Object.keys(boat_parsed_obj);

        var membership_parsed_obj = JSON.parse(
          JSON.stringify(this.memberships)
        );
        // Strip display-only fields so they are not sent to the membership write.
        MEMBERSHIP_DISPLAY_ONLY_FIELDS.forEach(
          (field) => delete membership_parsed_obj[field]
        );
        var membership_keynames = Object.keys(membership_parsed_obj);

        data = {};

        console.log("isRenew?", this.isRenew);
        if (this.isRenew) {
          return this.finishAuthorizenetRenewal(opp_guid, {
            account_parsed_obj,
            account_keynames,
            contact_parsed_obj,
            contact_keynames,
            boat_parsed_obj,
            boat_keynames,
            membership_parsed_obj,
            membership_keynames,
          });
        }

        return this.finishAuthorizenetNewMember(opp_guid, {
          account_parsed_obj,
          account_keynames,
          contact_parsed_obj,
          contact_keynames,
          boat_parsed_obj,
          boat_keynames,
          membership_parsed_obj,
          membership_keynames,
        });
      }
    },
    async finishAuthorizenetRenewal(opp_guid, {
      account_parsed_obj,
      account_keynames,
      contact_parsed_obj,
      contact_keynames,
      boat_parsed_obj,
      boat_keynames,
      membership_parsed_obj,
      membership_keynames,
    }) {
      var data = {};

      //If the user no longer wants ARB but has it on currently...

      this.autorenew_status =
        this.autorenew_status === "true" ||
        this.autorenew_status === true;

      //if the data being passed during a renewal (this.memberships.auto_renew__c) is true, then we want to cancel the ARB no matter what
      let needToCancelARBs = this.memberships.auto_renew__c;

      console.log("needToCancelARBs", needToCancelARBs);

      if (needToCancelARBs) {
        console.log(
          "Cancelling ARBs as this user already has one and is renewing"
        );

        console.log(
          "Attempting to delete subId with id ",
          this.membership_sfid
        );
        // Make DELETE request to cancel ARB and await it
        try {
          const cancelResponse = await cancelArbs(this.membership_sfid);
          console.log(
            "ARB cancellation successful:",
            cancelResponse.data
          );
        } catch (error) {
          console.error(
            "Error cancelling ARB, cannot continue",
            error
          );
          //display toast
          this.$bvToast.toast("Error cancelling ARB: " + error, {
            variant: "danger",
            title: "Error cancelling ARB",
            autoHideDelay: 5000,
          });
          this.disableSubmit = false;
          return;
        }
      }
      this.disableSubmit = true;
      this.$bvToast.toast("Starting the renewal process.", {
        variant: "info",
        title: "Starting renewal.",
        autoHideDelay: 5000,
      });
      var sfid_parsed_obj = JSON.parse(JSON.stringify(this.routes));
      var sfid_keynames = Object.keys(sfid_parsed_obj);

      data["heroku_external_id__c"] = opp_guid;
      data["name"] = "Pending Invoice Number";
      data["closedate"] = new Date().toISOString();
      data["stagename"] = "Invoice Open";
      data["accountid"] = this.account_sfid;
      data["membership__c"] = this.membership_sfid;

      //add a referring member/account if it exists
      if (this.referring_account != null) {
        data["referring_account__c"] = this.referring_account;
      }

      if (this.campaign_state_value) {
        data["campaignid"] = this.campaign_sfid;
      }

      postOpportunity(data).then((response) => {
        console.log("opportunity response: " + response);

        data = {};

        data["contactid"] = this.contact_sfid;
        data["opportunity__heroku_external_id__c"] = opp_guid;
        data["role"] = "Primary Member";
        postContactRole(data).then(async (response) => {
          console.log("contactroles response: " + response);

          //TODO - ADD LOGIC FOR ACTIVATION CODE PRODUCT
          const selected_products = [];
          // Get product IDs from catalog
          const product_ids = this.getProductIds();

          // For trials, use the selected trial product ID, otherwise use catalog
          if (this.CardSelection.includes("Trial")) {
            console.log(
              "Adding trial product to selected_products"
            );
            selected_products.push(
              this.selected_trial_time_product
            );
          } else {
            console.log(
              "Adding membership product to selected_products"
            );
            selected_products.push(
              product_ids[this.CardSelection.toLowerCase()]
            );
          }

          if (
            this.TrailerSelection != "None" &&
            this.TrailerSelection != ""
          ) {
            console.log(
              "Adding trailer care product to selected_products"
            );
            selected_products.push(
              product_ids[this.TrailerSelection.toLowerCase()]
            );
          } else {
            console.log("No TC selected");
          }

          if (this.donation_amount > 0) {
            selected_products.push(product_ids.donation);
            console.log(
              "add donation product to selected_products"
            );
          }

          if (this.activation_codes?.value__c > 0) {
            console.log(
              "Activation code found, adding to selected products"
            );
            selected_products.push(product_ids.activation);
          }

          // Create promises for all OLI insertions
          const oliPromises = selected_products.map(
            (element, key, arr) => {
              data = {};
              data.opportunity__heroku_external_id__c = opp_guid;
              data.quantity = 1;
              // Set product details using productCatalog
              const productDetails =
                this.getProductDetails(element);

              if (productDetails) {
                // Standard products from catalog
                data.pricebookentryid =
                  productDetails.pricebookentryid;
                data.product2id = productDetails.product2id;
                data.listprice = productDetails.price || 0;

                // Set unitprice based on product type
                const product_ids = this.getProductIds();
                if (element === product_ids.activation) {
                  // Activation code special logic - unitprice must be negative (discount)
                  console.log("Activation code found, setting up ");
                  data.unitprice = -(this.activation_codes
                    .value__c > this.card_price
                    ? this.card_price
                    : this.activation_codes.value__c);
                  data.boxes_cards__c = this.activation_codes.sfid;
                } else if (element === product_ids.donation) {
                  // Donation uses custom amount
                  data.unitprice = this.donation_amount;
                } else if (
                  element === product_ids.marine ||
                  element === product_ids.universal
                ) {
                  // Trailer care products
                  data.unitprice = this.trailering_price;
                } else {
                  // Membership cards use card_price
                  data.unitprice = this.card_price;
                }
              } else {
                console.log(
                  "product not found in catalog:",
                  element
                );
              }

              // Apply promotion code for membership cards
              const product_ids_promo = this.getProductIds();
              const isMembershipCard = [
                product_ids_promo.gold,
                product_ids_promo.lake,
                product_ids_promo.profmariner,
                product_ids_promo.commercial,
              ].includes(element);

              if (this.promotion_valid && isMembershipCard) {
                if (this.promotion_sfid != null) {
                  // Get membership pricebook entry IDs from catalog
                  const membershipPBEs = [
                    this.productCatalog.membership?.gold
                      ?.pricebook_entry_id,
                    this.productCatalog.membership?.lake
                      ?.pricebook_entry_id,
                    this.productCatalog.membership?.profmariner
                      ?.pricebook_entry_id,
                    this.productCatalog.membership?.commercial
                      ?.pricebook_entry_id,
                  ].filter(Boolean);

                  if (
                    membershipPBEs.includes(data.pricebookentryid)
                  ) {
                    console.log(
                      "applying promotion code on product, where element is: " +
                        element +
                        " and pricebookentryid is: " +
                        data.pricebookentryid
                    );
                    data.promotion_code__c = this.promotion_sfid;
                  }
                }
              } else {
                // Not a membership card, remove promotion code
                delete data.promotion_code__c;
                console.log(
                  "promotion code only applied on card type"
                );
              }

              if (Object.is(arr.length - 1, key)) {
                console.log(
                  `Last callback call at index ${key} with value ${element}`
                );
                data.final_product__c = true;
              } else {
                data.final_product__c = false;
              }

              // Add is_renewal flag for trial products (renewal flow)
              data.is_renewal = true;

              // Return the axios promise
              return postOpportunityLineItem(data);
            }
          );

          // Wait for ALL OLI insertions to complete
          await Promise.all(oliPromises);
          console.log(
            "All opportunity line items inserted successfully (Authorize.net renewal flow)"
          );

          // Now insert payment record AFTER all OLIs are done
          data = {};
          const dateObj = new Date();
          const month = dateObj.getUTCMonth() + 1;
          const day = dateObj.getUTCDate();
          const year = dateObj.getUTCFullYear();
          const newdate = `${month}/${day}/${year}`;

          if (
            (this.funds_collected_locally == false ||
              this.funds_collected_locally == "false") &&
            this.transId
          ) {
            console.log(
              "inside response of chargeauthedcard RENEWAL flow"
            );

            data.pymt__processor_connection__c =
              STRIPE_PROCESSOR_CONNECTION_SFID;
            data.pymt__log__c = "Payment via Stripe";
            data.pymt__payment_processor__c = "Stripe";
            data.pymt__payment_type__c = "Credit Card";
            data.pymt__card_type__c = this.GetCardType(
              this.memberships.card_number__c
            );
            data.pymt__last_4_digits__c = this.GetLastFour(
              this.memberships.card_number__c
            );
            data.pymt__transaction_id__c = this.transId;
            data.pymt__authorization_id__c = this.auth_code;
            data.pymt__account__c = this.account_sfid;
            data.pymt__contact__c = this.contact_sfid;
            data.pymt__opportunity__r__heroku_external_id__c =
              opp_guid;
            data.pymt__status__c = "Completed";
            data.pymt__amount__c = this.paymentAmount;
            data.pymt__date__c = newdate;
            data.herokudirect__c = false;
            data.name = "Payment via Membership App";
            data.create_source__c =
              process.env.VUE_APP_CREATE_SOURCE;
            data.processed_by_username = this.cachedName;
            data.consent = this.consentObject;

            console.log("payment data:");
            console.log(data);

            await postPayment(this.attachActivationCode(data));
            console.log("renewal payments insertion result");
          } else {
            // Funds collected locally, $0, or no successful charge (no transId)
            // → record as Credit, never Credit Card, to avoid DI "hybrid" records.
            console.log(
              "funds are collected locally, insert credit payment"
            );

            // NOTE: the /payments API requires pymt__processor_connection__c and
            // pymt__payment_processor__c (reqparse required=True) — omitting them
            // makes the endpoint silently return 200 without inserting. Keep them.
            data["pymt__processor_connection__c"] =
              STRIPE_PROCESSOR_CONNECTION_SFID;
            data["pymt__payment_type__c"] = "Credit";
            data["pymt__log__c"] = "Credit Payment";
            data["pymt__payment_processor__c"] = "Stripe";
            data["pymt__account__c"] = this.account_sfid;
            data["pymt__contact__c"] = this.contact_sfid;
            data["pymt__opportunity__r__heroku_external_id__c"] =
              opp_guid;
            data["pymt__status__c"] = "Completed";
            data["pymt__amount__c"] = this.paymentAmount;
            data["pymt__date__c"] = newdate;
            data["name"] = "Credit Payment via Membership App";
            data["create_source__c"] =
              process.env.VUE_APP_CREATE_SOURCE;
            data["processed_by_username"] = this.cachedName;
            data["consent"] = this.consentObject;

            await postPayment(this.attachActivationCode(data));
            console.log("Credit payments insertion result");

            this.trackUxcamSubmit("succeeded", {
              path: "authorizenet_renewal",
            });
            this.showModal();
            this.$bvToast.toast(
              "The member was renewed succesfully. The form has been reset.",
              {
                variant: "success",
                title: "Member renewed successfully.",
                autoHideDelay: 5000,
              }
            );
          }
        });
      });

      data = {};
      sfid_keynames
        .filter(
          (v) => (v.endsWith("s") && v != "arbs") || v == "account"
        )
        .forEach((field) => {
          if (field == "account") {
            field = "accounts";
            account_keynames.forEach((field) => {
              data[field] = account_parsed_obj[field];
            });

            data["sfid"] = this.account_sfid;
          } else if (field == "boats") {
            boat_keynames.forEach((field) => {
              data[field] = boat_parsed_obj[field];
            });
            data["sfid"] = this.boat_sfid;
          } else if (field == "memberships") {
            membership_keynames.forEach((field) => {
              data[field] = membership_parsed_obj[field];
            });
            data["sfid"] = this.membership_sfid;
          } else if (field == "contacts") {
            contact_keynames.forEach((field) => {
              data[field] = contact_parsed_obj[field];
            });
            data["sfid"] = this.contact_sfid;
          }

          const patchFn = {
            accounts: patchAccount,
            boats: patchBoat,
            memberships: patchMembership,
            contacts: patchContact,
          }[field];
          patchFn(data)
            .then((response) => {
              console.log(response);
            })
            .catch((error) => {
              this.$bvToast.toast(
                `The following error occured: ${error}`,
                {
                  variant: "danger",
                  title: "An Error Occured",
                  autoHideDelay: 5000,
                }
              );
            });

          data = {};
        });

      this.$bvToast.toast("Update successful.", {
        title: "Membership updated",
        variant: "success",
        autoHideDelay: 5000,
      });

      data = {};
      data["to_list"] = this.getEmailList;
      data["body"] =
        this.account.name +
        " has been successfully renewed." +
        "\n\n" +
        this.getEmailBody;
      data["subject"] =
        "Member successfully renewed from Membership App";

      console.log("attempting to send email1");
      console.log(data);

      this.showModal();

      postEmail(data).then((response) => {
        console.log(response);
      });
    },
    finishAuthorizenetNewMember(opp_guid, {
      account_parsed_obj,
      account_keynames,
      contact_parsed_obj,
      contact_keynames,
      boat_parsed_obj,
      boat_keynames,
      membership_parsed_obj,
      membership_keynames,
    }) {
      var data = {};

      console.log("starting new submission");
      const acc_guid = this.guid();
      data["heroku_external_id__c"] = acc_guid;
      data["account_detail_type__c"] = "Customer - Retail";
      data["type"] = "General";
      data["name"] = this.account.name;
      data["recordtypeid"] = "01237000000Tgx2AAC";
      account_keynames.forEach((field) => {
        data[field] = account_parsed_obj[field];
      });

      postAccount(data).then((response) => {
        data = {};

        if (!("error" in response)) {
          const cont_guid = this.guid();
          data["account__heroku_external_id__c"] = acc_guid; // Account ID
          data["recordtypeid"] = RECORDTYPE_PERSON_CONTACT;
          data["heroku_external_id__c"] = cont_guid;
          contact_keynames.forEach((field) => {
            data[field] = contact_parsed_obj[field];
          });

          postContact(data).then((response) => {
            data = {};
            if (!("error" in response)) {
              const memb_guid = this.guid();
              data["account__r__heroku_external_id__c"] = acc_guid;
              data["membership_contact__r__heroku_external_id__c"] =
                cont_guid;
              data["heroku_external_id__c"] = memb_guid;

              membership_keynames.forEach((field) => {
                data[field] = membership_parsed_obj[field];
              });

              postMembership(data).then((response) => {
                data = {};
                if (!("error" in response)) {
                  const boat_guid = this.guid();

                  data["heroku_external_id__c"] = boat_guid;
                  data["account__r__heroku_external_id__c"] =
                    acc_guid;
                  data["contact__r__heroku_external_id__c"] =
                    cont_guid;
                  data[
                    "related_membership__r__heroku_external_id__c"
                  ] = memb_guid;

                  boat_keynames.forEach((field) => {
                    data[field] = boat_parsed_obj[field];
                  });

                  postBoat(data).then((response) => {
                    data = {};
                    if (!("error" in response)) {
                      data["heroku_external_id__c"] = opp_guid;
                      data["name"] = "Pending Invoice Number";
                      data["closedate"] = new Date().toISOString();
                      data["stagename"] = "Invoice Open";
                      data["account__heroku_external_id__c"] =
                        acc_guid;
                      data["membership__r__heroku_external_id__c"] =
                        memb_guid;

                      if (this.campaign_state_value) {
                        data["campaignid"] = this.campaign_sfid;
                      }

                      //add a referring member/account if it exists
                      if (this.referring_account != null) {
                        data["referring_account__c"] =
                          this.referring_account;
                      }

                      postOpportunity(data).then((response) => {
                        if (!("error" in response)) {
                          console.log("opp insert");
                          console.log(response);
                          data = {};

                          data["contact__heroku_external_id__c"] =
                            cont_guid;
                          data[
                            "opportunity__heroku_external_id__c"
                          ] = opp_guid;
                          data["role"] = "Primary Member";
                          postContactRole(data).then(async (response) => {
                            if (!("error" in response)) {
                              // Insert Opportunity Line Items
                              const selected_products =
                                this.buildSelectedProducts();
                              await this.createOLIPromises(
                                selected_products,
                                opp_guid,
                                false
                              ); // isRenewal = false
                              console.log(
                                "All opportunity line items inserted successfully (Authorize.net new member flow)"
                              );

                              // Now insert payment record AFTER all OLIs are done
                              data = {};
                              var dateObj = new Date();
                              var month = dateObj.getUTCMonth() + 1;
                              var day = dateObj.getUTCDate();
                              var year = dateObj.getUTCFullYear();
                              var newdate =
                                month + "/" + day + "/" + year;

                              if (
                                (this.funds_collected_locally ===
                                  false ||
                                  this.funds_collected_locally ===
                                    "false") &&
                                this.transId
                              ) {
                                data[
                                  "pymt__processor_connection__c"
                                ] = STRIPE_PROCESSOR_CONNECTION_SFID;
                                data["pymt__log__c"] = "";
                                data["pymt__payment_processor__c"] =
                                  "Stripe";
                                data["pymt__payment_type__c"] =
                                  "Credit Card";
                                data["pymt__card_type__c"] =
                                  this.GetCardType(
                                    this.memberships.card_number__c
                                  );
                                data["pymt__last_4_digits__c"] =
                                  this.memberships.card_number__c ==
                                  null
                                    ? ""
                                    : this.GetLastFour(
                                        this.memberships
                                          .card_number__c
                                      );
                                data["pymt__transaction_id__c"] =
                                  this.transId;
                                data["pymt__authorization_id__c"] =
                                  this.auth_code;
                                data[
                                  "pymt__account__r__heroku_external_id__c"
                                ] = acc_guid;
                                data[
                                  "pymt__contact__r__heroku_external_id__c"
                                ] = cont_guid;
                                data[
                                  "pymt__opportunity__r__heroku_external_id__c"
                                ] = opp_guid;
                                data["pymt__status__c"] =
                                  "Completed";
                                data["pymt__amount__c"] =
                                  this.paymentAmount;
                                data["pymt__date__c"] = newdate;
                                data["herokudirect__c"] = false;
                                data["name"] =
                                  "Payment via Membership App";
                                data["create_source__c"] =
                                  process.env.VUE_APP_CREATE_SOURCE;
                                data["processed_by_username"] =
                                  this.cachedName;
                                data["consent"] =
                                  this.consentObject;

                                console.log("payment data:");
                                console.log(data);

                                await postPayment(this.attachActivationCode(data));
                                console.log(
                                  "payments insertion result"
                                );
                              } else {
                                // Funds collected locally, $0, or no successful
                                // charge (no transId) → record as Credit, never
                                // Credit Card, to avoid DI "hybrid" records.
                                console.log(
                                  "funds are collected locally, insert credit payment"
                                );

                                // NOTE: /payments API requires
                                // pymt__processor_connection__c and
                                // pymt__payment_processor__c (reqparse
                                // required=True) — omitting them makes the
                                // endpoint silently 200 without inserting. Keep them.
                                data[
                                  "pymt__processor_connection__c"
                                ] = STRIPE_PROCESSOR_CONNECTION_SFID;
                                data["pymt__payment_type__c"] =
                                  "Credit";
                                data["pymt__log__c"] =
                                  "Credit Payment";
                                data["pymt__payment_processor__c"] =
                                  "Stripe";
                                data[
                                  "pymt__account__r__heroku_external_id__c"
                                ] = acc_guid;
                                data[
                                  "pymt__contact__r__heroku_external_id__c"
                                ] = cont_guid;
                                data[
                                  "pymt__opportunity__r__heroku_external_id__c"
                                ] = opp_guid;
                                data["pymt__status__c"] =
                                  "Completed";
                                data["pymt__amount__c"] =
                                  this.paymentAmount;
                                data["pymt__date__c"] = newdate;
                                data["name"] =
                                  "Credit Payment via Membership App";
                                data["create_source__c"] =
                                  process.env.VUE_APP_CREATE_SOURCE;
                                data["processed_by_username"] =
                                  this.cachedName;
                                data["consent"] =
                                  this.consentObject;

                                await postPayment(this.attachActivationCode(data));
                                console.log(
                                  "Credit payments insertion result"
                                );

                                this.$bvToast.toast(
                                  "The member was inserted succesfully. The form has been reset.",
                                  {
                                    variant: "success",
                                    title:
                                      "Member inserted successfully.",
                                    autoHideDelay: 5000,
                                  }
                                );
                              }

                              // Show success message and send email AFTER payment is complete
                              this.trackUxcamSubmit("succeeded", {
                                path: "authorizenet_new_member",
                              });
                              this.showModal();
                              this.$bvToast.toast(
                                "The member was inserted succesfully. The form has been reset.",
                                {
                                  variant: "success",
                                  title:
                                    "Member inserted successfully.",
                                  autoHideDelay: 5000,
                                }
                              );

                              data = {};
                              data["to_list"] = this.getEmailList;
                              data["body"] =
                                this.account.name +
                                " has been successfully submitted into the database." +
                                "\n\n" +
                                this.getEmailBody;
                              data["subject"] =
                                "Membership successfully inserted from Membership App";

                              console.log(
                                "attempting to send email"
                              );
                              console.log(data);

                              postEmail(data).then((response) => {
                                console.log(response);
                              });
                            }
                          });
                        }
                      });
                    }
                  });
                }
              });
            }
          });
        }
      });
    },
  },
};
